import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Slider,
  Switch,
  Typography,
  Alert,
  CircularProgress,
  FormGroup,
  Divider,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import { adminApi, useGetActiveMockPostJobsQuery, useCancelMockPostJobMutation } from '../../redux/adminApiSlice';
import NeighborhoodSelector from './NeighborhoodSelector';
import PlaceSelector from './PlaceSelector';
import { Place, Neighborhood } from '../../types/interfaces';
import CustomPostCreator from './CustomPostCreator';

type ExpirationTypes = '30m' | '1h' | '6h' | '12h' | '24h' | '48h' | 'infinity';
type PromotionLevel = 'PLACE' | 'NEIGHBORHOOD' | 'CITY' | 'STATE' | 'COUNTRY';

// Available photo categories based on S3 folder structure
const PHOTO_CATEGORIES = [
  { id: 'general', label: 'General', description: 'General photos' },
  { id: 'thirst_trap', label: 'Thirst Trap', description: 'Attractive/provocative photos' },
  { id: 'selfie', label: 'Selfie', description: 'Self-portraits and personal photos' },
  { id: 'animals', label: 'Animals', description: 'Pet and animal photos' },
  { id: 'celebrities', label: 'Celebrities', description: 'Celebrity and famous people photos' },
  { id: 'food', label: 'Food', description: 'Food and dining photos' },
  { id: 'graduation', label: 'Graduation', description: 'Graduation and academic photos' },
  { id: 'memes', label: 'Memes', description: 'Funny and meme photos' },
  { id: 'places', label: 'Places', description: 'Location and venue photos' },
  { id: 'political', label: 'Political', description: 'Political and corporate photos' },
  { id: 'wedding', label: 'Wedding', description: 'Wedding and marriage photos' }
] as const;

interface MockPostSettings {
  count: number;
  withPhotos: boolean;
  withVideos: boolean;
  photoChance: number;
  videoChance: number;
  mediaFolder: string;
  enabledPhotoCategories: string[];
  maxPromotionLevel: number;
  promotionIntensity: number;
  withComments: boolean;
  commentChance: number;
  maxCommentsPerPost: number;
  expirationSettings: {
    types: Record<ExpirationTypes, boolean>;
    chances: Record<ExpirationTypes, number>;
  };
  useSavedPostText: boolean;
  enableRateLimit: boolean;
  durationMinutes: number; // Time period to spread posts over
}

const initialSettings: MockPostSettings = {
  count: 10,
  withPhotos: true,
  withVideos: false,
  photoChance: 0.3,
  videoChance: 0.1,
  mediaFolder: '',
  enabledPhotoCategories: ['general', 'selfie', 'food', 'places', 'memes'], // Default safe categories
  maxPromotionLevel: 1,
  promotionIntensity: 50,
  withComments: true,
  commentChance: 0.5,
  maxCommentsPerPost: 3,
  expirationSettings: {
    types: {
      '30m': true,
      '1h': true,
      '6h': true,
      '12h': true,
      '24h': true,
      '48h': true,
      'infinity': true
    },
    chances: {
      '30m': 0.10,
      '1h': 0.10,
      '6h': 0.15,
      '12h': 0.15,
      '24h': 0.20,
      '48h': 0.20,
      'infinity': 0.10
    }
  },
  useSavedPostText: true,
  enableRateLimit: false,
  durationMinutes: 60 // Default: spread posts over 1 hour
};

const LEVEL_ORDER: PromotionLevel[] = ['PLACE', 'NEIGHBORHOOD', 'CITY', 'STATE', 'COUNTRY'];

const PromotionLevelSlider: React.FC<{
  value: number; 
  intensity: number;
  onChange: (value: number) => void;
  onIntensityChange: (value: number) => void;
  disabled: boolean;
}> = ({ value, intensity, onChange, onIntensityChange, disabled }) => {
  const levelMarks = [
    { value: 0, label: 'Place' },
    { value: 1, label: 'Neighborhood' },
    { value: 2, label: 'City' },
    { value: 3, label: 'State' },
    { value: 4, label: 'Country' }
  ];

  return (
    <Box>
      <Typography id="promotion-level-slider" gutterBottom>
        Maximum Promotion Level: {levelMarks[value].label}
      </Typography>
      <Slider
        value={value}
        onChange={(_, newValue) => onChange(newValue as number)}
        aria-labelledby="promotion-level-slider"
        valueLabelDisplay="auto"
        step={1}
        marks={levelMarks}
        min={0}
        max={4}
        disabled={disabled}
      />
      
      <Typography id="promotion-intensity-slider" gutterBottom sx={{ mt: 2 }}>
        Promotion Intensity: {intensity}%
      </Typography>
      <Slider
        value={intensity}
        onChange={(_, newValue) => onIntensityChange(newValue as number)}
        aria-labelledby="promotion-intensity-slider"
        valueLabelDisplay="auto"
        step={5}
        marks={[
          { value: 0, label: 'Low' },
          { value: 50, label: 'Medium' },
          { value: 100, label: 'High' }
        ]}
        min={0}
        max={100}
        disabled={disabled}
      />
      
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Higher intensity means more posts will reach higher levels. At maximum intensity, most posts will reach the maximum level.
      </Typography>
    </Box>
  );
};

const MockPostCreator: React.FC = () => {
  const [settings, setSettings] = useState<MockPostSettings>(initialSettings);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<Neighborhood[]>([]);
  const [selectedPlaces, setSelectedPlaces] = useState<Place[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isNeighborhoodSelectorOpen, setIsNeighborhoodSelectorOpen] = useState(false);
  const [isPlaceSelectorOpen, setIsPlaceSelectorOpen] = useState(false);
  
  
  // Get saved mock posts
  const { data: savedMockPosts = [] } = adminApi.useGetSavedMockPostsQuery();
  
  // Create mock posts hook
  const [createMockPosts, { isLoading }] = adminApi.useCreateMockPostsMutation();
  const [cancelMockPostCreation] = adminApi.useCancelMockPostCreationMutation();
  
  // Job management hooks
  const { data: activeJobsData, refetch: refetchJobs } = useGetActiveMockPostJobsQuery(undefined, {
    pollingInterval: 5000, // Poll every 5 seconds when component is mounted
  });
  const [cancelJob] = useCancelMockPostJobMutation();
  
  const activeJobs = activeJobsData?.jobs || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSliderChange = (field: keyof MockPostSettings) => (event: Event, newValue: number | number[]) => {
    setSettings(prev => ({
      ...prev,
      [field]: typeof newValue === 'number' ? newValue : newValue[0]
    }));
  };

  const handlePromotionLevelChange = (newValue: number) => {
    setSettings(prev => ({
      ...prev,
      maxPromotionLevel: newValue
    }));
  };

  const handlePromotionIntensityChange = (newValue: number) => {
    setSettings(prev => ({
      ...prev,
      promotionIntensity: newValue
    }));
  };

  const handleExpirationTypeToggle = (type: ExpirationTypes) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      expirationSettings: {
        ...prev.expirationSettings,
        types: {
          ...prev.expirationSettings.types,
          [type]: event.target.checked
        }
      }
    }));
  };

  const handleExpirationChanceChange = (type: ExpirationTypes) => (event: Event, newValue: number | number[]) => {
    const value = typeof newValue === 'number' ? newValue : newValue[0];
    
    setSettings(prev => ({
      ...prev,
      expirationSettings: {
        ...prev.expirationSettings,
        chances: {
          ...prev.expirationSettings.chances,
          [type]: value
        }
      }
    }));
  };


  const handleSubmit = async () => {
    if (selectedNeighborhoods.length === 0 && selectedPlaces.length === 0) {
      setError('Please select at least one neighborhood or place');
      return;
    }

    if (settings.withPhotos && settings.enabledPhotoCategories.length === 0) {
      setError('Please select at least one photo category when using photos');
      return;
    }

    if (settings.enableRateLimit && (!settings.durationMinutes || settings.durationMinutes < 1)) {
      setError('Duration must be at least 1 minute when rate limiting is enabled');
      return;
    }

    setError(null);
    setSuccess(null);
    setIsCreating(true);

    try {
      // Convert our promotion level settings to the format expected by the backend
      const maxLevel = LEVEL_ORDER[settings.maxPromotionLevel] as PromotionLevel;
      const intensity = settings.promotionIntensity / 100; // Convert to 0-1 range
      
      // Calculate level chances based on the intensity and max level
      const levelChances: Record<string, number> = {};
      
      // Modified approach to ensure posts consistently get promotions to proper levels
      // Each level gets a chance based on intensity, ensuring posts always get some promotes
      for (let i = 0; i <= settings.maxPromotionLevel; i++) {
        const level = LEVEL_ORDER[i];
        
        if (i === 0) {
          // Origin level (PLACE) gets a guaranteed minimum chance, even at low intensity
          // This is because many posts should stay at their origin level
          const baseChance = 0.6 - (intensity * 0.4); // Decreases as intensity increases (0.6 to 0.2)
          levelChances[level] = Math.max(0.2, baseChance); // Minimum 20% chance
        } 
        else if (i === settings.maxPromotionLevel) {
          // Max level gets a chance directly proportional to intensity
          levelChances[level] = intensity * 0.6; // Up to 60% chance at max intensity
        } 
        else {
          // Middle levels get balanced chances, weighted by position and intensity
          // Lower levels get more chance than higher levels
          const positionFactor = 1 - (i / settings.maxPromotionLevel); // Higher for lower levels
          const baseChance = 0.2 * positionFactor; // Base chance weighted by position
          levelChances[level] = baseChance + (intensity * 0.3 * positionFactor); // Add intensity factor
        }
      }
      
      // Always ensure some chance for origin level if no other level is selected
      // This is important to prevent posts with 0 promotes
      let totalChance = Object.values(levelChances).reduce((sum, chance) => sum + chance, 0);
      if (totalChance < 0.95) {
        // If total chance is less than 95%, boost origin level chance
        levelChances[LEVEL_ORDER[0]] = levelChances[LEVEL_ORDER[0]] + (0.95 - totalChance);
      }
      
      // Log the final chances for debugging
      console.log('Final level chances:', Object.entries(levelChances).map(
        ([level, chance]) => `${level}: ${(chance * 100).toFixed(1)}%`
      ));
      
      const response = await createMockPosts({
        count: settings.count,
        withPhotos: settings.withPhotos,
        withVideos: settings.withVideos,
        photoChance: settings.photoChance,
        videoChance: settings.videoChance,
        mediaFolder: settings.mediaFolder,
        enabledPhotoCategories: settings.enabledPhotoCategories,
        withPromotes: true,
        maxPromotionLevel: maxLevel,
        promotionLevelChances: levelChances,
        withComments: settings.withComments,
        commentChance: settings.commentChance,
        maxCommentsPerPost: settings.maxCommentsPerPost,
        selectedNeighborhoods,
        selectedPlaces,
        expirationSettings: settings.expirationSettings,
        savedMockPosts,
        useSavedPostText: settings.useSavedPostText,
        enableRateLimit: settings.enableRateLimit,
        durationMinutes: settings.enableRateLimit ? settings.durationMinutes : undefined
      }).unwrap();

      if (response.jobId) {
        setSuccess(`Job started successfully! Job ID: ${response.jobId.substring(0, 8)}... Posts will be created in the background.`);
        await refetchJobs(); // Refresh jobs list
      } else {
        setSuccess(`Successfully created ${response.posts.length} mock posts`);
      }
    } catch (err) {
      setError(`Failed to create mock posts: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error creating mock posts:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = async () => {
    try {
      // Use the regular cancellation (for immediate post creation)
      await cancelMockPostCreation().unwrap();
      setSuccess('Mock post creation cancelled');
    } catch (err) {
      setError('Failed to cancel mock post creation');
      console.error('Error cancelling mock post creation:', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Create Mock Posts" />
        <Tab label="Custom Post Text" />
      </Tabs>
      
      {activeTab === 0 ? (
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          <Grid container spacing={3}>
            {/* Main Settings Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Mock Post Settings</Typography>
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  
                  {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                      {success}
                    </Alert>
                  )}
                  
                  {isCreating && (
                    <LinearProgress sx={{ mb: 2 }} />
                  )}
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography id="post-count-slider" gutterBottom>
                        Number of Posts: {settings.count}
                      </Typography>
                      <Slider
                        value={settings.count}
                        onChange={handleSliderChange('count')}
                        aria-labelledby="post-count-slider"
                        valueLabelDisplay="auto"
                        step={1}
                        marks
                        min={1}
                        max={1000}
                        disabled={isCreating}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" gutterBottom>Rate Limiting</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        When enabled, posts will be spread evenly over the specified time period to simulate realistic user posting behavior. The job runs in the background and continues even if you close this tab.
                      </Typography>
                      
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.enableRateLimit}
                              onChange={(e) => setSettings({ ...settings, enableRateLimit: e.target.checked })}
                              disabled={isCreating}
                            />
                          }
                          label="Enable Rate Limiting (Spread Posts Over Time)"
                        />
                        
                        {settings.enableRateLimit && (
                          <Box sx={{ pl: 3, mt: 2 }}>
                            <Typography id="duration-minutes-slider" gutterBottom>
                              Duration (Minutes): {settings.durationMinutes}
                            </Typography>
                            <Slider
                              value={settings.durationMinutes}
                              onChange={handleSliderChange('durationMinutes')}
                              aria-labelledby="duration-minutes-slider"
                              valueLabelDisplay="auto"
                              step={5}
                              marks={[
                                { value: 5, label: '5m' },
                                { value: 15, label: '15m' },
                                { value: 30, label: '30m' },
                                { value: 60, label: '1h' },
                                { value: 120, label: '2h' },
                                { value: 240, label: '4h' },
                                { value: 480, label: '8h' },
                                { value: 1440, label: '24h' }
                              ]}
                              min={1}
                              max={1440}
                              disabled={isCreating}
                            />
                            
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {settings.count} posts will be spread evenly over {settings.durationMinutes} minute{settings.durationMinutes !== 1 ? 's' : ''} 
                              ({settings.count > 0 && settings.durationMinutes > 0 ? 
                                `~${(settings.durationMinutes * 60 / settings.count).toFixed(1)} seconds between posts` : 
                                'N/A'})
                            </Typography>
                          </Box>
                        )}
                      </FormGroup>
                      
                      {/* Active Jobs List */}
                      {activeJobs.length > 0 && (
                        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Active Jobs ({activeJobs.length})
                          </Typography>
                          {activeJobs.map((job) => (
                            <Box key={job.jobId} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  Job {job.jobId.substring(0, 8)}...
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={async () => {
                                    try {
                                      await cancelJob(job.jobId).unwrap();
                                      await refetchJobs();
                                      setSuccess('Job cancelled successfully');
                                    } catch (err) {
                                      setError('Failed to cancel job');
                                    }
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={job.progress} 
                                  />
                                </Box>
                                <Box sx={{ minWidth: 35 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {job.progress.toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Typography variant="body2" color="text.secondary">
                                Created: {job.createdPosts} / {job.totalPosts} posts
                              </Typography>
                              {job.estimatedCompletionTime && (
                                <Typography variant="body2" color="text.secondary">
                                  Estimated completion: {new Date(job.estimatedCompletionTime).toLocaleTimeString()}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" gutterBottom>Media Settings</Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.withPhotos}
                              onChange={(e) => setSettings({ ...settings, withPhotos: e.target.checked })}
                              disabled={isCreating}
                            />
                          }
                          label="Include Photos"
                        />
                        
                        {settings.withPhotos && (
                          <Box sx={{ pl: 3, mt: 1 }}>
                            <Typography id="photo-chance-slider" gutterBottom>
                              Photo Chance: {Math.round(settings.photoChance * 100)}%
                            </Typography>
                            <Slider
                              value={settings.photoChance}
                              onChange={handleSliderChange('photoChance')}
                              aria-labelledby="photo-chance-slider"
                              valueLabelDisplay="auto"
                              step={0.05}
                              min={0.05}
                              max={1}
                              disabled={isCreating}
                            />
                          </Box>
                        )}
                        
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.withVideos}
                              onChange={(e) => setSettings({ ...settings, withVideos: e.target.checked })}
                              disabled={isCreating}
                            />
                          }
                          label="Include Videos"
                        />
                        
                        {settings.withVideos && (
                          <Box sx={{ pl: 3, mt: 1 }}>
                            <Typography id="video-chance-slider" gutterBottom>
                              Video Chance: {Math.round(settings.videoChance * 100)}%
                            </Typography>
                            <Slider
                              value={settings.videoChance}
                              onChange={handleSliderChange('videoChance')}
                              aria-labelledby="video-chance-slider"
                              valueLabelDisplay="auto"
                              step={0.05}
                              min={0.05}
                              max={1}
                              disabled={isCreating}
                            />
                          </Box>
                        )}
                        
                        {(settings.withPhotos || settings.withVideos) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Photo Categories
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Select which photo categories to use for mock posts. Posts will automatically use appropriate categories based on their content.
                            </Typography>
                            <FormGroup>
                              {PHOTO_CATEGORIES.map((category) => (
                                <FormControlLabel
                                  key={category.id}
                                  control={
                                    <Switch
                                      checked={settings.enabledPhotoCategories.includes(category.id)}
                                      onChange={(e) => {
                                        const newCategories = e.target.checked
                                          ? [...settings.enabledPhotoCategories, category.id]
                                          : settings.enabledPhotoCategories.filter(id => id !== category.id);
                                        setSettings({ ...settings, enabledPhotoCategories: newCategories });
                                      }}
                                      disabled={isCreating}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography variant="body2">{category.label}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {category.description}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              ))}
                            </FormGroup>
                            {settings.enabledPhotoCategories.length === 0 && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                At least one photo category should be selected when using photos.
                              </Alert>
                            )}
                          </Box>
                        )}
                      </FormGroup>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" gutterBottom>Promotion Settings</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Posts can only be promoted up to the maximum level selected and will never go below their origin level.
                        PLACE posts can reach any enabled level, while NEIGHBORHOOD posts cannot go below NEIGHBORHOOD level.
                      </Typography>
                      
                      <PromotionLevelSlider
                        value={settings.maxPromotionLevel}
                        intensity={settings.promotionIntensity}
                        onChange={handlePromotionLevelChange}
                        onIntensityChange={handlePromotionIntensityChange}
                        disabled={isCreating}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" gutterBottom>Comment Settings</Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.withComments}
                              onChange={(e) => setSettings({ ...settings, withComments: e.target.checked })}
                              disabled={isCreating}
                            />
                          }
                          label="Include Comments"
                        />
                        
                        {settings.withComments && (
                          <Box sx={{ pl: 3, mt: 1 }}>
                            <Typography id="comment-chance-slider" gutterBottom>
                              Comment Chance: {Math.round(settings.commentChance * 100)}%
                            </Typography>
                            <Slider
                              value={settings.commentChance}
                              onChange={handleSliderChange('commentChance')}
                              aria-labelledby="comment-chance-slider"
                              valueLabelDisplay="auto"
                              step={0.05}
                              min={0.05}
                              max={1}
                              disabled={isCreating}
                            />
                            
                            <Typography id="max-comments-slider" gutterBottom>
                              Max Comments Per Post: {settings.maxCommentsPerPost}
                            </Typography>
                            <Slider
                              value={settings.maxCommentsPerPost}
                              onChange={handleSliderChange('maxCommentsPerPost')}
                              aria-labelledby="max-comments-slider"
                              valueLabelDisplay="auto"
                              step={1}
                              min={1}
                              max={10}
                              disabled={isCreating}
                            />
                          </Box>
                        )}
                      </FormGroup>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" gutterBottom>Custom Text Settings</Typography>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.useSavedPostText}
                              onChange={(e) => setSettings({ ...settings, useSavedPostText: e.target.checked })}
                              disabled={isCreating}
                            />
                          }
                          label="Use Saved Custom Post Text"
                        />
                        {settings.useSavedPostText && (
                          <Typography variant="caption" color="text.secondary" sx={{ pl: 3, mt: 0.5 }}>
                            Will use your custom post texts for locations that have them.
                            {savedMockPosts.length > 0 ? 
                              ` (${savedMockPosts.length} saved templates available)` : 
                              ' (No saved templates yet - go to Custom Post Text tab to create some)'}
                          </Typography>
                        )}
                      </FormGroup>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1" gutterBottom>Post Expiration</Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" gutterBottom>Enabled Types</Typography>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['30m']}
                                  onChange={handleExpirationTypeToggle('30m')}
                                  disabled={isCreating}
                                />
                              }
                              label="30 minutes"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['1h']}
                                  onChange={handleExpirationTypeToggle('1h')}
                                  disabled={isCreating}
                                />
                              }
                              label="1 hour"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['6h']}
                                  onChange={handleExpirationTypeToggle('6h')}
                                  disabled={isCreating}
                                />
                              }
                              label="6 hours"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['12h']}
                                  onChange={handleExpirationTypeToggle('12h')}
                                  disabled={isCreating}
                                />
                              }
                              label="12 hours"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['24h']}
                                  onChange={handleExpirationTypeToggle('24h')}
                                  disabled={isCreating}
                                />
                              }
                              label="24 hours"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['48h']}
                                  onChange={handleExpirationTypeToggle('48h')}
                                  disabled={isCreating}
                                />
                              }
                              label="48 hours"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={settings.expirationSettings.types['infinity']}
                                  onChange={handleExpirationTypeToggle('infinity')}
                                  disabled={isCreating}
                                />
                              }
                              label="Infinity (No Expiration)"
                            />
                          </FormGroup>
                        </Grid>
                        
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" gutterBottom>Probability Distribution</Typography>
                          {Object.entries(settings.expirationSettings.types)
                            .filter(([_, enabled]) => enabled)
                            .map(([type]) => (
                              <Box key={type} sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                  {type === '30m' ? '30 minutes' : 
                                   type === '1h' ? '1 hour' : 
                                   type === '6h' ? '6 hours' : 
                                   type === '12h' ? '12 hours' : 
                                   type === '24h' ? '24 hours' : 
                                   type === '48h' ? '48 hours' : 
                                   'Infinity'}: {Math.round(settings.expirationSettings.chances[type as ExpirationTypes] * 100)}%
                                </Typography>
                                <Slider
                                  value={settings.expirationSettings.chances[type as ExpirationTypes]}
                                  onChange={handleExpirationChanceChange(type as ExpirationTypes)}
                                  aria-labelledby={`expiration-chance-slider-${type}`}
                                  valueLabelDisplay="auto"
                                  step={0.05}
                                  min={0.05}
                                  max={1}
                                  disabled={isCreating}
                                />
                              </Box>
                            ))}
                            
                            {Object.values(settings.expirationSettings.types).filter(Boolean).length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                No expiration types enabled
                              </Typography>
                            )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Location Selectors */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Neighborhoods</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography>
                          {selectedNeighborhoods.length === 0 ? 
                            'No neighborhoods selected' : 
                            `${selectedNeighborhoods.length} neighborhood${selectedNeighborhoods.length > 1 ? 's' : ''} selected`}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          onClick={() => setIsNeighborhoodSelectorOpen(true)}
                          disabled={isCreating}
                        >
                          Select Neighborhoods
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Places</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography>
                          {selectedPlaces.length === 0 ? 
                            'No places selected' : 
                            `${selectedPlaces.length} place${selectedPlaces.length > 1 ? 's' : ''} selected`}
                        </Typography>
                        <Button 
                          variant="outlined" 
                          onClick={() => setIsPlaceSelectorOpen(true)}
                          disabled={isCreating}
                        >
                          Select Places
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleSubmit}
                  disabled={isCreating || isLoading || (selectedNeighborhoods.length === 0 && selectedPlaces.length === 0)}
                  startIcon={isCreating && <CircularProgress size={24} color="inherit" />}
                >
                  {isCreating 
                    ? (settings.enableRateLimit ? 'Starting Job...' : 'Creating...') 
                    : (settings.enableRateLimit 
                        ? `Create Mock Posts (${settings.durationMinutes} min)` 
                        : 'Create Mock Posts'
                      )
                  }
                </Button>
                
                {isCreating && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <CustomPostCreator 
            onSuccess={() => {
              setSuccess('Custom post text saved successfully');
            }}
          />
        </Box>
      )}
      
      {/* Dialogs */}
      <NeighborhoodSelector
        open={isNeighborhoodSelectorOpen}
        onClose={() => setIsNeighborhoodSelectorOpen(false)}
        selectedNeighborhoods={selectedNeighborhoods}
        onSelectionChange={setSelectedNeighborhoods}
        disabled={isCreating}
      />
      
      <PlaceSelector
        open={isPlaceSelectorOpen}
        onClose={() => setIsPlaceSelectorOpen(false)}
        selectedPlaces={selectedPlaces}
        onSelectionChange={setSelectedPlaces}
        disabled={isCreating}
      />
    </Box>
  );
};

export default MockPostCreator; 